import React, { useEffect, useState } from "react";

import moment from "moment";

import { formatDate } from "react-lib/utils/dateUtils";
import type { PickMainState } from "react-lib/apps/HISV3/common/CommonInterface";

import { AlertV4, ButtonV4, TabsV4, type TabsV4Item } from "../common/ui";
import { providerIdsOf, scopeProviderBlocks } from "../common/oaProvidersV4";
import { useOAProvidersV4 } from "../common/useOAProvidersV4";
import AppointmentCalendarV4 from "./AppointmentCalendarV4";
import AppointmentListV4 from "./AppointmentListV4";

import {
  LIST_ACTIONS,
  LIST_SEQUENCE,
  MAKE_SEQUENCE,
  emptyListFilter,
  type AppointmentListSlice,
  type MakeAppointmentSlice,
} from "./appointmentModelV4";

import "./_hisv4-appointment.scss";

export type AppointmentRunSequence = (params: Record<string, unknown>) => void;

export type AppointmentSetProp = (key: string, value: unknown, callback?: () => void) => void;

export type ItemSearchEvent = { message: "ItemSearch"; params: Record<string, unknown> };

export type CardAppointmentV4Props = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "searchedItemListWithKey"
> & {
  onEvent?: (event: ItemSearchEvent) => void;
  setProp?: AppointmentSetProp;
  runSequence: AppointmentRunSequence;
  apiToken?: string;
  divisionId?: number;
  AppointmentListMEHSequence?: AppointmentListSlice | null;
  MakeAppointmentMeHeySequence?: MakeAppointmentSlice | null;
};

type AppointmentTab = "list" | "make";

const TABS: TabsV4Item<AppointmentTab>[] = [
  { key: "list", label: "รายชื่อผู้นัดหมาย" },
  { key: "make", label: "ทำนัดหมาย" },
];

/** หน้า "นัดหมาย" ของ HIS V4: รายชื่อผู้นัดหมาย (AppointmentListMEH) · ทำนัดหมาย (MakeAppointmentMeHey) */
const CardAppointmentV4 = (props: CardAppointmentV4Props) => {
  const [tab, setTab] = useState<AppointmentTab>("list");
  const [result, setResult] = useState<string | null>(null);
  const [createdIds, setCreatedIds] = useState<(number | string)[]>([]);
  const providers = useOAProvidersV4(props.apiToken, props.divisionId);
  const providerIds = providerIdsOf(providers.doctors);
  const scoped = props.apiToken !== undefined;
  const make = scoped
    ? {
        ...props.MakeAppointmentMeHeySequence,
        allProviders: (props.MakeAppointmentMeHeySequence?.allProviders ?? []).filter((provider) =>
          providerIds.has(Number(provider.provider_id)),
        ),
        dsb:
          props.MakeAppointmentMeHeySequence?.dsb &&
          scopeProviderBlocks(props.MakeAppointmentMeHeySequence.dsb, providerIds),
      }
    : props.MakeAppointmentMeHeySequence;

  // Effect
  useEffect(() => {
    // ตัวกรองเริ่มว่างทุกช่อง แล้วค้นหา
    if (props.setProp) {
      props.setProp(
        "AppointmentListMEHSequence",
        {
          sequenceIndex: "Action",
          filterAppointmentList: emptyListFilter(),
          appointmentList: undefined,
          appointmentListSummaryPage: { activePage: 1, total: 1 },
        },
        () => {
          props.runSequence({ sequence: LIST_SEQUENCE, action: LIST_ACTIONS.SEARCH });
        },
      );
    } else {
      props.runSequence({ sequence: LIST_SEQUENCE, action: LIST_ACTIONS.SEARCH, restart: true });
    }

    props.runSequence({ sequence: MAKE_SEQUENCE, restart: true });

    return () => {
      props.runSequence({ sequence: MAKE_SEQUENCE, clear: true });
    };
  }, []);

  // Handler
  const handleChangeTab = (next: AppointmentTab) => {
    setResult(null);
    setTab(next);
  };

  const handleModalOpen = () => {
    setResult(null);
  };

  const handleCreated = (id: number | string) => {
    setCreatedIds((ids) => [...ids, id]);
  };

  // Render
  return (
    <div className={`hv4-appt${tab === "list" ? " hv4-appt--list" : ""}`}>
      <TabsV4 ariaLabel="นัดหมาย" items={TABS} active={tab} onChange={handleChangeTab} />
      <div className="hv4-page-head">
        <h1 className="hv4-h1">นัดหมาย</h1>
        <span className="hv4-page-sub">
          วันนี้ {formatDate(moment())} · นัดที่สร้างจากหน้า "คำสั่ง" จะขึ้นที่นี่อัตโนมัติ
        </span>
      </div>
      {!!result && (
        <div className="hv4-appt-result" role="status">
          {result}
        </div>
      )}
      {providers.error && (
        <AlertV4
          tone="danger"
          actions={
            <ButtonV4 variant="ghost" size="sm" onClick={providers.reload}>
              ลองใหม่
            </ButtonV4>
          }
        >
          {providers.error}
        </AlertV4>
      )}
      {tab === "list" ? (
        <AppointmentListV4
          list={props.AppointmentListMEHSequence}
          providers={make?.allProviders}
          searchedItemListWithKey={props.searchedItemListWithKey}
          buttonLoadCheck={props.buttonLoadCheck}
          errorMessage={props.errorMessage}
          runSequence={props.runSequence}
          setProp={props.setProp}
          onEvent={props.onEvent}
          createdIds={createdIds}
          onResult={setResult}
        />
      ) : (
        <AppointmentCalendarV4
          make={make}
          searchedItemListWithKey={props.searchedItemListWithKey}
          runSequence={props.runSequence}
          onEvent={props.onEvent}
          onResult={setResult}
          onModalOpen={handleModalOpen}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
};

CardAppointmentV4.displayName = "CardAppointmentV4";

export default CardAppointmentV4;
